// src/components/dashboard/TerminalDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Edit, Trash, Download, Plus, Check, AlertCircle, FileText, X, Filter, RefreshCw, Package, ClipboardList, LogOut, User } from 'lucide-react';
import RegistrationFormTerminal from './RegistrationFormTerminal';
import { formatDate } from '../../utils/date-formatter';
import { registrationService } from '../../services/registrationService';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  
  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md max-w-md z-50 flex items-center justify-between ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      <div className="flex items-center">
        {type === 'success' && <Check className="h-5 w-5 mr-2" />}
        {type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
        {type === 'info' && <FileText className="h-5 w-5 mr-2" />}
        <p>{message}</p>
      </div>
      <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const TerminalDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  
  const [filters, setFilters] = useState({
    number: '',

  });

  const [stats, setStats] = useState({
    total: 0,
  });

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 5000);
  };

  const fetchRegistrations = async (currentFilters = filters) => {
    try {
      setIsLoading(true);
      const response = await registrationService.getList(currentFilters);
      setRegistrations(response.results);
      setStats({ total: response.results.length });
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
      showNotification('Failed to fetch registrations', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedRegistration(null);
  };

  const handleRegistrationSubmit = async (formData) => {
    try {
      setIsLoading(true);
      if (selectedRegistration) {
        await registrationService.update(selectedRegistration.id, formData);
        showNotification(t('Registration_updated'), 'success');
      } else {
        await registrationService.create(formData);
        showNotification(t('Registration_created'), 'success');
      }
      await fetchRegistrations();
      handleFormClose();
    } catch (error) {
      console.error('Failed to save registration:', error);
      showNotification('Registration_failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditRegistration = async (id) => {
    try {
      const registration = registrations.find(reg => reg.id === id);
      if (registration) {
        setSelectedRegistration(registration);
        setIsFormOpen(true);
      }
    } catch (error) {
      console.error(`Failed to get registration ${id}:`, error);
      showNotification('Failed to get registration details', 'error');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await fetchRegistrations(filters);
  };

  const handleFilterReset = async () => {
    const resetFilters = {
      number: '',
    };
    setFilters(resetFilters);
    await fetchRegistrations(resetFilters);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        setIsLoading(true);
        await registrationService.delete(id);
        showNotification(t('Registration deleted successfully'), 'success');
        await fetchRegistrations();
      } catch (error) {
        console.error('Failed to delete registration:', error);
        showNotification('Failed to delete registration', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };


  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header with logout */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className="text-xl font-bold tracking-wide bg-primary-700 text-white px-3 py-1 rounded-md hover:bg-primary-800 transition-colors"
              >
                TTS
              </button>
              <span className="ml-4 text-lg font-semibold text-gray-800">{'Parking Dashboard'}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-md">
                <User className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">{user?.username || 'Guest'}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>{'Logout'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
      <Notification 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: '', type: '' })}
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{'Registrations'}</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <button
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-gray-500 mr-2" />
              <span className="text-lg font-medium text-gray-900">{'Filters'}</span>
            </div>
            <svg
              className={`h-5 w-5 text-gray-500 transform transition-transform ${isFilterExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {isFilterExpanded && (
          <form onSubmit={handleSearch} className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{'Vehicle Number'}</label>
                <input
                  type="text"
                  name="number"
                  value={filters.number}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder={'Search By Number'}
                />
              </div>

            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <Search className="h-4 w-4 mr-2" />
                {'Search'}
              </button>
              <button
                type="button"
                onClick={handleFilterReset}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {'Reset'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Truck Number'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Trailer Number'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Departure Date'}</th>
				{user?.role !== 'gugutivertex' && user?.role !== 'sadakhlovertex'  &&  user?.role !== 'murphy' && (
				<>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Natakhtari'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Natakhtari Entry Date'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Natakhtari Exit Date'}</th>					
				
				</>
				)}
				{user?.role !== 'gugutivertex' && user?.role !== 'sadakhlovertex'  &&  user?.role !== 'natakhtari' && (
				<>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Point'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Entry Date'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Exit Date'}</th>
				</>
				)}
				{user?.role !== 'murphy' && user?.role !== 'natakhtari' && (
				<>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Point'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Entry Date'}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{'Parking Exit Date'}</th>
				</>
				)}
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{'Actions'}</th>
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-5 w-5 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{'Loading'}</span>
                    </div>
                  </td>
                </tr>
              ) : registrations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p>{'No Registrations Found'}</p>
                    <p className="text-sm text-gray-400 mt-1">{'Try Changing Filters'}</p>
                  </td>
                </tr>
              ) : (
                registrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-primary-600 font-medium text-center">
                      {registration.number}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {registration.trailer_number || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.departure_date)}
                    </td>
					{user?.role !== 'gugutivertex' && user?.role !== 'sadakhlovertex'  && user?.role !== 'murphy' && (
					<>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {'Yes'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.natakhtari_entry_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.natakhtari_exit_date)}
                    </td>
					</>
					)}					
					
					
					
					{user?.role !== 'gugutivertex' && user?.role !== 'sadakhlovertex'  && user?.role !== 'natakhtari' && (
					<>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {t(registration.entry_point || '-')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.terminal_entry_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.terminal_exit_date)}
                    </td>
					</>
					)}
					{user?.role !== 'murphy' && user?.role !== 'natakhtari' && (
					<>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {t(registration.exit_point || '-')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.exitPoint_entry_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.exitPoint_exit_date)}
                    </td>
					</>
					)}
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditRegistration(registration.id)}
                          className="p-1 text-blue-600 hover:text-blue-900"
                          title={'edit'}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer */}
      {!isLoading && registrations.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 px-2 sm:px-4 py-3 sm:py-4 mt-0 rounded-b-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-full">
                <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <span className="text-base sm:text-lg font-semibold text-gray-800">
                {'Total'}: <span className="text-blue-600">{stats.total}</span>
              </span>
            </div>
          </div>
        </div>
      )}
      
      <RegistrationFormTerminal
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleRegistrationSubmit}
        initialData={selectedRegistration}
      />
        </div>
      </div>
    </div>
  );
};

export default TerminalDashboard;